import jsPDF from "jspdf";
import "jspdf-autotable";
import { drawReportHeader, stampPageNumbers } from "./reports/pdfChrome";
import { downloadBlob } from "./reports/downloadBlob";
import { buildReportFilename } from "./reports/reportFilename";

/**
 * Generate PDF report for warranty records
 * @param {Array} warranties - Array of warranty objects to include in report
 * @param {string} propertyName - Name of the property
 * @param {string} reportType - Type of report ("All Warranties", "Expiring Soon", "Expired", etc.)
 * @param {Object} filterInfo - Additional filter information for the report header
 * @param {string} generatedBy - Name of the user generating the report
 */
export const generateWarrantyReportPDF = (
  warranties,
  propertyName,
  reportType = "All Warranties",
  filterInfo = {},
  generatedBy = "Unknown"
) => {
  const doc = new jsPDF();

  // Standardized header (report type shown centered beneath the title)
  const contentTop = drawReportHeader(doc, {
    title: "Warranty Tracker Report",
    dateRange: reportType,
    generatedBy,
  });

  doc.setFontSize(12);
  doc.setFont(undefined, "normal");
  let startY = contentTop;
  doc.text(`Property: ${propertyName}`, 14, startY);

  // Add filter information if provided
  if (filterInfo.status && filterInfo.status !== "All") {
    startY += 6;
    doc.text(`Status Filter: ${filterInfo.status}`, 14, startY);
  }
  if (filterInfo.itemType && filterInfo.itemType !== "All") {
    startY += 6;
    doc.text(`Item Type Filter: ${filterInfo.itemType}`, 14, startY);
  }
  if (filterInfo.searchTerm) {
    startY += 6;
    doc.text(`Search: "${filterInfo.searchTerm}"`, 14, startY);
  }

  startY += 6;
  doc.setFontSize(10);
  doc.text(`Total Records: ${warranties.length}`, 14, startY);

  // Prepare table data
  const tableData = warranties.map((w) => {
    const daysLeft = w.daysRemaining < 0
      ? `Expired ${Math.abs(w.daysRemaining)}d ago`
      : `${w.daysRemaining} days`;

    return [
      w.serialNumber || "N/A",
      w.itemName || "N/A",
      w.itemType || "N/A",
      w.vendor || "N/A",
      w.poNumber || "N/A",
      w.purchaseDate || "N/A",
      w.warrantyEndDate || "N/A",
      daysLeft,
      w.statusLabel || "Unknown",
    ];
  });

  // Table configuration
  doc.autoTable({
    startY: startY + 8,
    head: [
      [
        "Serial #",
        "Item",
        "Type",
        "Vendor",
        "PO #",
        "Purchase",
        "Warranty End",
        "Days Left",
        "Status",
      ],
    ],
    body: tableData,
    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontStyle: "bold",
      halign: "center",
    },
    columnStyles: {
      0: { cellWidth: 22 }, // Serial #
      1: { cellWidth: 28 }, // Item
      2: { cellWidth: 22 }, // Type
      3: { cellWidth: 22 }, // Vendor
      4: { cellWidth: 15 }, // PO #
      5: { cellWidth: 20 }, // Purchase
      6: { cellWidth: 20 }, // Warranty End
      7: { cellWidth: 18 }, // Days Left
      8: { cellWidth: 18 }, // Status
    },
    didParseCell: (data) => {
      // Color code status column
      if (data.column.index === 8 && data.section === "body") {
        const warranty = warranties[data.row.index];
        if (warranty.daysRemaining < 0) {
          data.cell.styles.textColor = [244, 67, 54]; // Red
        } else if (warranty.daysRemaining <= 30) {
          data.cell.styles.textColor = [255, 152, 0]; // Orange
        } else if (warranty.daysRemaining <= 90) {
          data.cell.styles.textColor = [255, 193, 7]; // Yellow/Amber
        } else {
          data.cell.styles.textColor = [76, 175, 80]; // Green
        }
      }
    },
    margin: { top: 10, right: 14, bottom: 20, left: 14 },
  });

  // Standardized page numbers on every page
  stampPageNumbers(doc);

  // Save the PDF
  const fileName = buildReportFilename(`Warranty_Report_${propertyName}`, {
    ext: "pdf",
  });
  downloadBlob(doc.output("blob"), fileName);

  return fileName;
};

/**
 * Generate a summary PDF with statistics and expiring warranties
 * @param {Array} warranties - All warranty records
 * @param {string} propertyName - Name of the property
 */
export const generateWarrantySummaryPDF = (
  warranties,
  propertyName,
  generatedBy = "Unknown"
) => {
  const doc = new jsPDF();

  // Calculate statistics
  const stats = {
    total: warranties.length,
    active: warranties.filter((w) => w.daysRemaining > 30).length,
    expiringSoon: warranties.filter((w) => w.daysRemaining >= 0 && w.daysRemaining <= 30).length,
    expired: warranties.filter((w) => w.daysRemaining < 0).length,
  };

  const expiringSoonWarranties = warranties
    .filter((w) => w.daysRemaining >= 0 && w.daysRemaining <= 30)
    .sort((a, b) => a.daysRemaining - b.daysRemaining);

  // Standardized header
  const contentTop = drawReportHeader(doc, {
    title: "Warranty Summary Report",
    generatedBy,
  });

  doc.setFontSize(12);
  doc.setFont(undefined, "normal");
  doc.text(`Property: ${propertyName}`, 14, contentTop);

  // Statistics boxes
  let yPos = contentTop + 14;
  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
  doc.text("Warranty Statistics", 14, yPos);

  yPos += 10;
  doc.setFontSize(10);
  doc.setFont(undefined, "normal");

  // Total
  doc.rect(14, yPos, 45, 15);
  doc.text("Total Warranties", 17, yPos + 6);
  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text(stats.total.toString(), 17, yPos + 12);

  // Active
  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  doc.rect(64, yPos, 45, 15);
  doc.setTextColor(76, 175, 80); // Green
  doc.text("Active", 67, yPos + 6);
  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text(stats.active.toString(), 67, yPos + 12);

  // Expiring Soon
  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  doc.setTextColor(255, 152, 0); // Orange
  doc.rect(114, yPos, 45, 15);
  doc.text("Expiring Soon", 117, yPos + 6);
  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text(stats.expiringSoon.toString(), 117, yPos + 12);

  // Expired
  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  doc.setTextColor(244, 67, 54); // Red
  doc.rect(164, yPos, 45, 15);
  doc.text("Expired", 167, yPos + 6);
  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text(stats.expired.toString(), 167, yPos + 12);

  // Reset text color
  doc.setTextColor(0, 0, 0);

  // Expiring Soon Section
  if (expiringSoonWarranties.length > 0) {
    yPos += 25;
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text("Warranties Expiring Soon (Next 30 Days)", 14, yPos);

    const expiringTableData = expiringSoonWarranties.map((w) => [
      w.itemName || "N/A",
      w.serialNumber || "N/A",
      w.warrantyEndDate || "N/A",
      `${w.daysRemaining} days`,
    ]);

    doc.autoTable({
      startY: yPos + 5,
      head: [["Item", "Serial Number", "Expires On", "Days Remaining"]],
      body: expiringTableData,
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [255, 152, 0], // Orange
        textColor: 255,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 45 },
        2: { cellWidth: 40 },
        3: { cellWidth: 35 },
      },
      margin: { left: 14, right: 14 },
    });
  } else {
    yPos += 25;
    doc.setFontSize(12);
    doc.setTextColor(76, 175, 80); // Green
    doc.text("No warranties expiring in the next 30 days", 14, yPos);
  }

  // Standardized page numbers on every page
  stampPageNumbers(doc);

  // Save the PDF
  const fileName = buildReportFilename(`Warranty_Summary_${propertyName}`, {
    ext: "pdf",
  });
  downloadBlob(doc.output("blob"), fileName);

  return fileName;
};
